import React from 'react';
import { Alert, Button, Empty, Tag } from 'antd';
import RecordRow from './RecordRow.jsx';
import { rowState } from './validation.js';

/**
 * Generic per-section review page: title, selection count, the section's
 * records as a `RecordRow` list, an attention banner when anything is
 * flagged, and back/continue navigation. One instance renders whichever
 * section the wizard (Task 10) is currently on.
 *
 * `indices` (optional, section-local i -> original bucket index, defaults
 * to identity): the wizard groups `proposal[bucket]` into per-section item
 * arrays, but `verdicts` stay keyed to the ORIGINAL bucket index the
 * backend produced them against. Without this mapping, `rowState` would
 * look up a verdict at the section-local position and flag the wrong row
 * (or none at all) whenever a section isn't a leading prefix of its
 * bucket. `selected`/`onSelectionChange` are unaffected — they're
 * section-local sets the wizard owns and reconciles against the bucket
 * itself when it needs original indices (e.g. for `computeExcluded`).
 */
export default function ReviewSection({
  section, items, schemas, verdicts, bucket, indices, selected, onSelectionChange, onBack, onContinue,
}) {
  const states = items.map((_, i) => rowState(bucket, indices?.[i] ?? i, verdicts));
  const flagged = states.filter((s) => s.level !== 'ok').length;

  const toggle = (index) => {
    const next = new Set(selected);
    if (next.has(index)) next.delete(index); else next.add(index);
    onSelectionChange(next);
  };

  return (
    <div>
      <h2>{section.title}</h2>
      <Tag color="cyan">{selected.size} of {items.length} selected</Tag>

      {flagged > 0 && (
        <Alert
          type="warning"
          showIcon
          message={`${flagged} record${flagged === 1 ? '' : 's'} need${flagged === 1 ? 's' : ''} attention before commit`}
          style={{ margin: '12px 0' }}
        />
      )}

      {items.length === 0 ? (
        <Empty description="Nothing to import in this section" style={{ margin: '24px 0' }} />
      ) : (
        <div style={{ margin: '12px 0' }}>
          {items.map((item, i) => (
            <RecordRow
              key={i}
              item={item}
              schemas={schemas}
              state={states[i]}
              checked={selected.has(i)}
              onToggle={() => toggle(i)}
            />
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <Button onClick={onBack}>Back</Button>
        <Button type="primary" onClick={onContinue}>Continue &rarr;</Button>
      </div>
    </div>
  );
}
